import { weeklyDrop } from './homepage'
import { orderV3Config } from './orderV3'
import { bankTransferConfig, getBankTransferConfirmationSnapshot } from './payment'
import type { PublicProductType } from '../../shared/productFamily'
import type { ProductImageFraming } from '../../shared/productPresentation'

export type BusinessProductType = PublicProductType
export type BusinessProductAvailability = 'available' | 'sold-out' | 'unavailable'

export type BusinessProduct = {
  id: string
  name: string
  type: BusinessProductType
  description: string
  image: string
  imageAlt?: string
  whatsInside: string[]
  approxWeightG: number | null
  ingredients: string
  allergens: string[]
  mayContainAllergens: string[]
  gallery: Array<{ src: string; alt: string; framing: ProductImageFraming }>
  framing: ProductImageFraming
  enabled: boolean
  displayOrder: number
  isNew: boolean
  clickTarget: string
  hoverBackground: string
  availability: BusinessProductAvailability
  weeklyMenuIncluded: boolean
  orderable: boolean
}

export type BusinessPack = {
  quantity: number
  label: string
  price: number
  available: boolean
}

const runtimeWeeklyProducts: BusinessProduct[] = []
const runtimePacks: BusinessPack[] = []

export const businessData = {
  currency: 'NZD' as const,
  ordering: {
    enabled: true,
  },
  products: runtimeWeeklyProducts,
  packs: runtimePacks,
  delivery: {
    enabled: true,
    checkoutPath: '/checkout',
    cartPendingLabel: 'Calculated at checkout',
    cartHeading: 'We deliver up to 35 km from our Christchurch delivery base.',
    cartBody: 'Delivery fee is calculated by distance at checkout.',
    checkoutSummaryBody: 'Delivery fee is calculated by distance at checkout. Saturday & Sunday delivery, 3:00 pm – 6:00 pm.',
    areaSummary: weeklyDrop.deliveryAreas,
    deliveryWindow: weeklyDrop.deliveryWindow,
    addressAutocomplete: {
      provider: 'google' as const,
      country: 'nz' as const,
      publishableKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '',
    },
    days: [
      { id: 'saturday', label: 'Saturday', window: '3:00 pm – 6:00 pm', available: true },
      { id: 'sunday', label: 'Sunday', window: '3:00 pm – 6:00 pm', available: true },
    ],
  },
  payment: {
    methods: [
      { id: 'card', label: 'Pay securely online', available: true },
      { id: 'bank-transfer', label: 'Bank transfer', available: true },
    ],
    card: {
      available: true,
      secureNote: 'Secure online payment powered by Stripe',
      ctaLabel: 'Continue to secure payment',
    },
    bankTransfer: {
      available: true,
      get accountName() {
        return getBankTransferConfirmationSnapshot()?.accountName ?? bankTransferConfig.accountName
      },
      get accountNumber() {
        return getBankTransferConfirmationSnapshot()?.accountNumber ?? bankTransferConfig.accountNumber
      },
      pendingMessage: bankTransferConfig.pendingMessage,
      checkoutInstructions: bankTransferConfig.checkoutInstructions,
      instructions: bankTransferConfig.checkoutInstructions,
      ctaLabel: 'PLACE ORDER →',
    },
  },
  checkout: {
    reassurance: [
      'Secure checkout',
      'No account needed',
      'Fresh-baked to order',
    ],
    api: {
      enabled: import.meta.env.VITE_CHECKOUT_API_ENABLED === 'true',
      deliveryQuotePath: '/api/checkout/delivery-quote',
      hostedCheckoutPath: '/api/checkout/checkout-session',
      bankTransferOrderPath: '/api/checkout/bank-transfer-order',
    },
  },
  orderWindow: {
    closeMessage: weeklyDrop.orderClose,
    countdown: orderV3Config.countdown,
  },
} as const

export function setWeeklyOrderProducts(products: readonly BusinessProduct[]) {
  runtimeWeeklyProducts.splice(0, runtimeWeeklyProducts.length, ...products)
}

export function clearWeeklyOrderProducts() {
  runtimeWeeklyProducts.splice(0, runtimeWeeklyProducts.length)
}

export function setRuntimePackPricing(packs: readonly { quantity: number; priceCents: number }[]) {
  runtimePacks.splice(
    0,
    runtimePacks.length,
    ...packs.map((pack) => ({
      quantity: pack.quantity,
      label: `${pack.quantity}-Pack`,
      price: pack.priceCents / 100,
      available: true,
    })),
  )
}

export function clearRuntimePackPricing() {
  runtimePacks.splice(0, runtimePacks.length)
}

export function getBusinessProducts(): readonly BusinessProduct[] {
  return runtimeWeeklyProducts
}

export function getWeeklyOrderProducts(): BusinessProduct[] {
  return runtimeWeeklyProducts
}

export function getAvailablePacks(): BusinessPack[] {
  return runtimePacks.filter((pack) => pack.available)
}

export function getPackLabel(quantity: number): string {
  return runtimePacks.find((pack) => pack.quantity === quantity)?.label ?? `${quantity}-Pack`
}
